import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { DEFAULT_FLAGS, type FlagName, FLAGS, type FlagsState } from './flags';

const STORAGE_KEY = 'rudder-ai:flags';
const ENABLED = import.meta.env.DEV;

interface FlagsActions {
  setFlag: (name: FlagName, value: boolean) => void;
  resetAll: () => void;
}

const FlagsStateContext = createContext<FlagsState | null>(null);
const FlagsActionsContext = createContext<FlagsActions | null>(null);

function loadFromStorage(): FlagsState {
  if (!ENABLED || typeof window === 'undefined') return DEFAULT_FLAGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FLAGS;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const merged: FlagsState = { ...DEFAULT_FLAGS };
    for (const name of Object.keys(FLAGS) as FlagName[]) {
      const value = parsed[name];
      if (typeof value === 'boolean') merged[name] = value;
    }
    return merged;
  } catch {
    return DEFAULT_FLAGS;
  }
}

export function FeatureFlagsProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [state, setState] = useState<FlagsState>(loadFromStorage);

  useEffect(() => {
    if (!ENABLED || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore quota/serialization errors — dev-only utility
    }
  }, [state]);

  const actions = useMemo<FlagsActions>(
    () => ({
      setFlag: (name, value) => {
        if (!ENABLED) return;
        setState((prev) => ({ ...prev, [name]: value }));
      },
      resetAll: () => {
        if (!ENABLED) return;
        setState(DEFAULT_FLAGS);
      },
    }),
    [],
  );

  return (
    <FlagsActionsContext.Provider value={actions}>
      <FlagsStateContext.Provider value={state}>{children}</FlagsStateContext.Provider>
    </FlagsActionsContext.Provider>
  );
}

export function useFlag(name: FlagName): boolean {
  const state = useContext(FlagsStateContext);
  if (!state) throw new Error('useFlag must be used inside FeatureFlagsProvider');
  return state[name];
}

export function useAllFlags(): FlagsState {
  const state = useContext(FlagsStateContext);
  if (!state) throw new Error('useAllFlags must be used inside FeatureFlagsProvider');
  return state;
}

export function useFeatureFlagsActions(): FlagsActions {
  const actions = useContext(FlagsActionsContext);
  if (!actions) throw new Error('useFeatureFlagsActions must be used inside FeatureFlagsProvider');
  return actions;
}
